import type {
  PermittedMediaTypes,
  TodosSubmissionsOverride,
} from "@/components/MyStudents/Submissions/components/SubmissionsInformation/types";
import { BYTES_PER_UNIT, UPLOAD_TODO_IDS } from "@/components/MyStudents/Submissions/constants";
import type {
  StoredFileUpload,
  UploadRules,
  UploadSubmissionDataBuilder,
} from "@/components/MyStudents/Submissions/types";

export const formatFileSize = (bytes?: number): string => {
  if (bytes == null) {
    return "";
  }

  const KB = 1024;
  const MB = KB * 1024;

  if (bytes < KB) {
    return `${bytes} B`;
  }
  if (bytes < MB) {
    return `${(bytes / KB).toFixed(0)} KB`;
  }
  return `${(bytes / MB).toFixed(1)} MB`;
};

export const parseMaxFileSize = (value: string | undefined): number | undefined => {
  const match = value?.trim().match(/^(\d+(?:\.\d+)?)\s*(kb|mb|gb)$/i);
  return match ? Math.round(Number(match[1]) * BYTES_PER_UNIT[match[2].toLowerCase()]) : undefined;
};

const formatSizeLabel = (value: string): string =>
  value.trim().replace(/(kb|mb|gb)$/i, (unit) => ` ${unit.toUpperCase()}`);

export const getUploadRules = (permittedMediaTypes: PermittedMediaTypes | undefined): UploadRules => {
  const media = permittedMediaTypes?.media ?? [];
  const extensions = [...new Set(media.flatMap((entry) => entry.file_type))];
  const sizes = media
    .map((entry) => ({ bytes: parseMaxFileSize(entry.max_file_size), label: entry.max_file_size }))
    .filter((size): size is { bytes: number; label: string } => size.bytes !== undefined);
  const largest = sizes.sort((a, b) => b.bytes - a.bytes)[0];
  return {
    extensions,
    maxBytes: largest?.bytes,
    maxSizeLabel: largest ? formatSizeLabel(largest.label) : undefined,
  };
};

export const formatExtensionList = (extensions: string[]): string => {
  const labels = extensions.map((extension) => extension.toUpperCase());
  if (labels.length <= 2) {
    return labels.join(" or ");
  }
  return `${labels.slice(0, -1).join(", ")}, or ${labels[labels.length - 1]}`;
};

export const getUploadHint = (rules: UploadRules): string =>
  [formatExtensionList(rules.extensions), rules.maxSizeLabel ? `up to ${rules.maxSizeLabel}` : undefined]
    .filter(Boolean)
    .join(" · ");

export const getAcceptAttribute = (rules: UploadRules): string =>
  rules.extensions.map((extension) => `.${extension}`).join(",");

export const getFileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf(".");
  return lastDot === -1 ? "" : fileName.slice(lastDot + 1).toLowerCase();
};

export const validateUploadFile = (file: File, rules: UploadRules): string | undefined => {
  let error: string | undefined;
  if (!rules.extensions.includes(getFileExtension(file.name))) {
    error = `“${file.name}” isn't a supported file type. Upload a ${formatExtensionList(rules.extensions)}.`;
  } else if (rules.maxBytes !== undefined && file.size > rules.maxBytes) {
    error = `“${file.name}” is larger than the ${rules.maxSizeLabel} limit.`;
  }
  return error;
};

const asDocumentUpload: UploadSubmissionDataBuilder = (upload) => ({ document_upload: [upload] });

const asSupplementalFile =
  (ordinal: number): UploadSubmissionDataBuilder =>
  (upload) => ({ document_upload: [{ ...upload, ordinal }] });

const UPLOAD_SUBMISSION_DATA_BUILDERS: Record<string, UploadSubmissionDataBuilder> = {
  [UPLOAD_TODO_IDS.COVER_LETTER]: asDocumentUpload,
  [UPLOAD_TODO_IDS.GRADE_REPORT]: asDocumentUpload,
  [UPLOAD_TODO_IDS.HEADSHOT]: asDocumentUpload,
  [UPLOAD_TODO_IDS.LDM_REGISTRATION_POLICY]: asDocumentUpload,
  [UPLOAD_TODO_IDS.RESUME]: asDocumentUpload,
  [UPLOAD_TODO_IDS.STATEMENT_OF_PURPOSE]: asDocumentUpload,
  [UPLOAD_TODO_IDS.UNOFFICIAL_TRANSCRIPT]: asDocumentUpload,
  [UPLOAD_TODO_IDS.SUPPLEMENTAL_FORM_1]: asSupplementalFile(1),
  [UPLOAD_TODO_IDS.SUPPLEMENTAL_FORM_2]: asSupplementalFile(2),
  [UPLOAD_TODO_IDS.SUPPLEMENTAL_FORM_3]: asSupplementalFile(3),
  [UPLOAD_TODO_IDS.SUPPLEMENTAL_FORM_4]: asSupplementalFile(4),
  [UPLOAD_TODO_IDS.VISA_AND_PERMITS]: (upload) => ({ image_upload: [upload] }),
  [UPLOAD_TODO_IDS.PASSPORT]: (upload) => ({ passports: [{ ordinal: 1, image_upload: [upload] }] }),
};

export const hasUploadSubmissionMapping = (todoId: string | undefined): boolean =>
  !!todoId && todoId in UPLOAD_SUBMISSION_DATA_BUILDERS;

export const canTodoAcceptUploads = (todoSubmission: TodosSubmissionsOverride | null | undefined): boolean =>
  !!todoSubmission?.permitted_media_types?.media?.length &&
  todoSubmission.render_mode === "legacy" &&
  hasUploadSubmissionMapping(todoSubmission.todo_id);

export const buildUploadSubmissionData = (
  todoId: string,
  applicationId: string,
  upload: StoredFileUpload,
): Record<string, unknown> => {
  const build = UPLOAD_SUBMISSION_DATA_BUILDERS[todoId];
  if (!build) {
    throw new Error(`No upload submission mapping for to-do ${todoId}`);
  }
  return { application_id: applicationId, ...build(upload) };
};
